import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Check, ShieldCheck } from 'lucide-react';
import ShieldLogo from '../components/ShieldLogo';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TrustPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [trustData, setTrustData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchTrustData();
  }, [id]);

  const fetchTrustData = async () => {
    try {
      // Public endpoint — explicitly skip the Authorization header so
      // Bearer tokens from logged-in users don't accidentally cause a 401
      // path on intermediate proxies. Empty Authorization is treated as
      // unauthenticated by FastAPI which is correct for this public route.
      const response = await axios.get(`${API}/trust/${id}`, {
        transformRequest: [(data, headers) => {
          if (headers) delete headers.Authorization;
          return data;
        }],
        withCredentials: false,
      });
      setTrustData(response.data);
      setLoading(false);
      // Track trust page view (fire-and-forget; ignore errors)
      axios.post(`${API}/trust/${id}/view`, null, {
        transformRequest: [(data, headers) => {
          if (headers) delete headers.Authorization;
          return data;
        }],
        withCredentials: false,
      }).catch(() => {});
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      // Surface real backend status in console so prod issues are debuggable
      // eslint-disable-next-line no-console
      console.error('[TrustPage] fetch failed', { status, detail, id, url: `${API}/trust/${id}` });
      if (status === 404 && detail === 'Trust page not ready') {
        setError('This trust page is still being generated. Please refresh in a moment.');
      } else if (status === 404) {
        setError('Trust page not found');
      } else {
        setError(`Could not load trust page (${status || 'network error'})`);
      }
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <p className="text-[#1a1a1a]/60">Loading...</p>
      </div>
    );
  }

  if (error || !trustData) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <p className="text-[#1a1a1a] mb-4">{error || 'Trust page not found'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Verilock verification banner */}
      <div
        data-testid="trust-page-verified-banner"
        className="bg-[#0a5c44] text-white"
      >
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-2.5 flex items-center justify-center gap-2 text-xs sm:text-sm">
          <ShieldCheck className="w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0" />
          <span>This security page is verified and maintained by Verilock</span>
        </div>
      </div>

      {/* Header */}
      <div className="border-b border-[#e0ddd8]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 sm:py-5 flex items-center justify-between gap-3">
          <div className="inline-flex items-center gap-2">
            <ShieldLogo size={24} />
            <span className="text-2xl font-semibold" style={{ fontFamily: 'Instrument Serif, serif' }}>
              Verilock
            </span>
          </div>
          <span
            data-testid="trust-page-live-badge"
            className="inline-flex items-center gap-2 bg-[#0a5c44]/8 border border-[#0a5c44]/25 text-[#0a5c44] text-xs font-semibold uppercase tracking-wide px-2.5 py-1 rounded-full"
          >
            <span className="relative flex w-2 h-2">
              <span className="absolute inset-0 inline-flex w-full h-full rounded-full bg-[#0a5c44] opacity-60 animate-ping"></span>
              <span className="relative inline-flex w-2 h-2 rounded-full bg-[#0a5c44]"></span>
            </span>
            Live
          </span>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        {/* Company Profile */}
        <div className="mb-10 sm:mb-12">
          <h2 className="text-3xl sm:text-4xl font-normal mb-2 leading-tight" style={{ fontFamily: 'Instrument Serif, serif' }}>
            Security & Compliance
          </h2>
          <p
            data-testid="trust-page-last-updated"
            className="text-xs sm:text-sm text-[#1a1a1a]/50 mb-4 sm:mb-6"
          >
            Last updated: {new Date(trustData.created_at).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            })}
          </p>
          <p className="text-base sm:text-lg lg:text-xl text-[#1a1a1a]/80 leading-relaxed">
            {trustData.company_profile}
          </p>
        </div>

        {/* Trust Summary */}
        <div className="bg-[#f5f3ef] border border-[#e0ddd8] rounded-xl p-6 sm:p-8 mb-10 sm:mb-12">
          <h3 className="text-xl sm:text-2xl font-semibold mb-3 sm:mb-4 leading-snug" style={{ fontFamily: 'Instrument Serif, serif' }}>
            Our Commitment to Security
          </h3>
          <p className="text-base sm:text-lg text-[#1a1a1a]/70 leading-relaxed">
            {trustData.trust_summary}
          </p>
        </div>

        {/* Policies in Place */}
        <div>
          <h3 className="text-xl sm:text-2xl font-semibold mb-5 sm:mb-6 leading-snug" style={{ fontFamily: 'Instrument Serif, serif' }}>
            Policies & Documentation
          </h3>
          <div className="grid sm:grid-cols-3 gap-5 sm:gap-6">
            {trustData.policies.data_handling && (
              <div className="bg-white border border-[#e0ddd8] rounded-xl p-5 sm:p-6">
                <Check className="w-6 h-6 text-[#0a5c44] mb-3" />
                <h4 className="text-base sm:text-lg font-semibold mb-2 leading-snug">
                  {trustData.policies.data_handling}
                </h4>
                <p className="text-sm text-[#1a1a1a]/60">
                  In place and regularly reviewed
                </p>
              </div>
            )}
            
            {trustData.policies.access_control && (
              <div className="bg-white border border-[#e0ddd8] rounded-xl p-5 sm:p-6">
                <Check className="w-6 h-6 text-[#0a5c44] mb-3" />
                <h4 className="text-base sm:text-lg font-semibold mb-2 leading-snug">
                  {trustData.policies.access_control}
                </h4>
                <p className="text-sm text-[#1a1a1a]/60">
                  In place and regularly reviewed
                </p>
              </div>
            )}
            
            {trustData.policies.incident_response && (
              <div className="bg-white border border-[#e0ddd8] rounded-xl p-5 sm:p-6">
                <Check className="w-6 h-6 text-[#0a5c44] mb-3" />
                <h4 className="text-base sm:text-lg font-semibold mb-2 leading-snug">
                  {trustData.policies.incident_response}
                </h4>
                <p className="text-sm text-[#1a1a1a]/60">
                  In place and regularly reviewed
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="mt-12 sm:mt-16 pt-6 sm:pt-8 border-t border-[#e0ddd8] text-center">
          <p className="text-sm text-[#1a1a1a]/60">
            Last updated: {new Date(trustData.created_at).toLocaleDateString('en-US', { 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}
          </p>
          <p className="text-xs text-[#1a1a1a]/50 mt-3">
            Powered by{' '}
            <a
              href="https://verilock.ai"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="trust-page-powered-by-link"
              className="text-[#0a5c44] hover:underline font-medium"
            >
              Verilock
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default TrustPage;
